import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../context/AuthContext';
import { useMessagingWebSocket } from '../../hooks/useMessagingWebSocket';
import { messagingService } from '../../services/messaging.service';
import { theme } from '../../theme';
import MessagingConversationList from '../client/messaging/shared/MessagingConversationList';
import MessagingThreadView from '../client/messaging/shared/MessagingThreadView';

const noop = () => {};
const normalizeItems = (data) => (
  Array.isArray(data) ? data : data?.results || []
);
const unreadTotal = (items) => items.reduce(
  (total, item) => total + Number(item.unread_count || 0),
  0,
);

const SupportChatPanel = ({ onUnreadCountChange = noop }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdminOrSuperAdmin = ['admin', 'superadmin'].includes(user?.role);
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const [subject, setSubject] = useState('');
  const [initialMessage, setInitialMessage] = useState('');
  const [priority, setPriority] = useState('medium');
  const selectedIdRef = useRef(selectedId);
  const translateRef = useRef(t);
  const requestRef = useRef(0);

  selectedIdRef.current = selectedId;
  translateRef.current = t;

  const publishConversations = useCallback((updater) => {
    setConversations((current) => {
      const next = typeof updater === 'function' ? updater(current) : updater;
      onUnreadCountChange(unreadTotal(next));
      return next;
    });
  }, [onUnreadCountChange]);

  const loadConversations = useCallback(async () => {
    const requestId = ++requestRef.current;
    setLoading(true);
    try {
      const response = isAdminOrSuperAdmin
        ? await messagingService.listConversations({})
        : await messagingService.getMyConversations({
          archived: showArchived,
        });
      if (requestId !== requestRef.current) return;
      publishConversations(normalizeItems(response.data));
      setError('');
    } catch (_requestError) {
      if (requestId !== requestRef.current) return;
      setError(translateRef.current(
        'messaging.load_error',
        'Impossible de charger les demandes au support.',
      ));
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [isAdminOrSuperAdmin, publishConversations, showArchived]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    if (!selectedId) return;
    const conversationId = selectedId;
    let active = true;
    setDetail(null);
    setError('');

    const loadDetail = async () => {
      try {
        const response = await messagingService.retrieveConversation(
          conversationId,
        );
        if (!active || Number(selectedIdRef.current) !== Number(conversationId)) {
          return;
        }
        setDetail(response.data);
        await messagingService.markAllMessagesAsRead(conversationId);
        publishConversations((items) => items.map((item) => (
          Number(item.id) === Number(conversationId)
            ? { ...item, unread_count: 0 }
            : item
        )));
      } catch (_requestError) {
        if (!active) return;
        setError(translateRef.current(
          'messaging.load_error',
          'Impossible de charger cette demande.',
        ));
      }
    };

    loadDetail();
    return () => {
      active = false;
    };
  }, [publishConversations, selectedId]);

  const handleSocketMessage = useCallback((data) => {
    if (!data) return;
    if (data.type === 'new_conversation') {
      loadConversations();
      return;
    }
    if (data.type !== 'new_message' || !data.message) return;

    const incoming = data.message;
    const isOpen = Number(selectedIdRef.current)
      === Number(incoming.conversation_id);
    publishConversations((items) => items.map((item) => (
      Number(item.id) === Number(incoming.conversation_id)
        ? {
          ...item,
          last_message: incoming,
          unread_count: isOpen
            ? 0
            : Number(item.unread_count || 0) + 1,
        }
        : item
    )));

    if (!isOpen) return;
    setDetail((current) => {
      if (!current) return current;
      const messages = current.messages || [];
      if (messages.some((item) => item.id === incoming.id)) return current;
      return { ...current, messages: [...messages, incoming] };
    });
    messagingService.markAllMessagesAsRead(
      incoming.conversation_id,
    ).catch(noop);
  }, [loadConversations, publishConversations]);

  const { status: socketStatus, sendJson } = useMessagingWebSocket({
    enabled: Boolean(user?.token),
    token: user?.token,
    onMessage: handleSocketMessage,
  });

  useEffect(() => {
    if (!selectedId || socketStatus !== 'open') return;
    sendJson({
      action: 'join_conversation',
      conversation_id: selectedId,
    });
  }, [selectedId, sendJson, socketStatus]);

  const normalizedMessages = (detail?.messages || []).map((item) => ({
    ...item,
    sender_type: item.sender_type || (
      Number(item.sender?.id || item.sender_id) === Number(user?.id)
        ? 'professional'
        : 'support'
    ),
  }));

  const send = async () => {
    const content = draft.trim();
    if (!content || !selectedId || sending) return;
    setDraft('');
    setSending(true);
    setError('');

    const optimistic = {
      id: `tmp-${Date.now()}`,
      conversation_id: selectedId,
      sender_type: 'professional',
      sender: { id: user?.id },
      content,
      created_at: new Date().toISOString(),
    };
    setDetail((current) => current ? {
      ...current,
      messages: [...(current.messages || []), optimistic],
    } : current);

    try {
      const sent = sendJson({
        action: 'send_message',
        conversation_id: selectedId,
        content,
      });
      if (!sent) {
        const response = await messagingService.sendMessage(
          selectedId,
          content,
        );
        if (response?.data) {
          setDetail((current) => current ? {
            ...current,
            messages: [
              ...(current.messages || []).filter(
                (item) => item.id !== optimistic.id,
              ),
              response.data,
            ],
          } : current);
        }
      }
      loadConversations();
    } catch (_requestError) {
      setDraft(content);
      setDetail((current) => current ? {
        ...current,
        messages: (current.messages || []).filter(
          (item) => item.id !== optimistic.id,
        ),
      } : current);
      setError(translateRef.current(
        'messaging.send_error',
        'Impossible d’envoyer le message.',
      ));
    } finally {
      setSending(false);
    }
  };

  const createConversation = async () => {
    const cleanSubject = subject.trim();
    const cleanMessage = initialMessage.trim();
    if (!cleanSubject || !cleanMessage || sending) return;
    setSending(true);
    setError('');
    try {
      await messagingService.createConversation({
        subject: cleanSubject,
        initial_message: cleanMessage,
        priority,
      });
      setCreating(false);
      setSubject('');
      setInitialMessage('');
      setPriority('medium');
      loadConversations();
    } catch (_requestError) {
      setError(translateRef.current(
        'messaging.send_error',
        'Impossible de créer la demande.',
      ));
    } finally {
      setSending(false);
    }
  };

  const toggleArchive = async () => {
    if (!detail?.id || isAdminOrSuperAdmin) return;
    try {
      if (detail.is_archived) {
        await messagingService.unarchiveConversation(detail.id);
      } else {
        await messagingService.archiveConversation(detail.id);
      }
      setSelectedId(null);
      setDetail(null);
      loadConversations();
    } catch (_requestError) {
      setError('Impossible de modifier cette demande.');
    }
  };

  if (creating) {
    return (
      <Box
        sx={{
          height: '100%',
          overflowY: 'auto',
          p: 2,
          backgroundColor: theme.colors.secondary[50],
        }}
      >
        <Button
          startIcon={<ArrowBackRoundedIcon />}
          onClick={() => setCreating(false)}
          sx={{ mb: 1.5, color: theme.colors.primary[900] }}
        >
          Retour
        </Button>
        <Typography
          variant="h6"
          sx={{ mb: 2, fontWeight: 800, color: theme.colors.secondary[950] }}
        >
          Nouvelle demande
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        <TextField
          fullWidth
          size="small"
          label="Sujet"
          value={subject}
          onChange={(event) => setSubject(event.target.value)}
          sx={{ mb: 1.5 }}
        />
        <TextField
          select
          fullWidth
          size="small"
          label="Priorité"
          value={priority}
          onChange={(event) => setPriority(event.target.value)}
          sx={{ mb: 1.5 }}
        >
          <MenuItem value="low">Faible</MenuItem>
          <MenuItem value="medium">Normale</MenuItem>
          <MenuItem value="high">Haute</MenuItem>
          <MenuItem value="urgent">Urgente</MenuItem>
        </TextField>
        <TextField
          fullWidth
          multiline
          minRows={4}
          label="Message initial"
          value={initialMessage}
          onChange={(event) => setInitialMessage(event.target.value)}
          sx={{ mb: 1.5 }}
        />
        <Button
          fullWidth
          variant="contained"
          onClick={createConversation}
          disabled={!subject.trim() || !initialMessage.trim() || sending}
          sx={{
            borderRadius: '14px',
            textTransform: 'none',
            fontWeight: 800,
          }}
        >
          Envoyer
        </Button>
      </Box>
    );
  }

  if (loading && !conversations.length) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', height: '100%' }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (detail) {
    return (
      <MessagingThreadView
        identity={detail.subject || 'Support SALOONE'}
        subtitle="Support SALOONE"
        status={detail.status}
        messages={normalizedMessages}
        ownSenderType="professional"
        draft={draft}
        onDraftChange={setDraft}
        onSend={send}
        sending={sending}
        error={error}
        onBack={() => {
          setSelectedId(null);
          setDetail(null);
          setDraft('');
          setError('');
        }}
        actions={!isAdminOrSuperAdmin ? (
          <Button
            size="small"
            onClick={toggleArchive}
            sx={{
              color: theme.colors.primary[950],
              textTransform: 'none',
              fontWeight: 700,
            }}
          >
            {detail.is_archived ? 'Désarchiver' : 'Archiver'}
          </Button>
        ) : null}
      />
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          pt: 1.5,
        }}
      >
        {!isAdminOrSuperAdmin && (
          <Chip
            size="small"
            label={showArchived ? 'Archivées' : 'Actives'}
            variant={showArchived ? 'outlined' : 'filled'}
            onClick={() => setShowArchived((current) => !current)}
            sx={{ fontWeight: 700 }}
          />
        )}
        {!isAdminOrSuperAdmin && (
          <Button
            size="small"
            startIcon={<AddRoundedIcon />}
            onClick={() => setCreating(true)}
            aria-label="Nouvelle demande"
            sx={{
              ml: 'auto',
              color: theme.colors.primary[900],
              textTransform: 'none',
              fontWeight: 800,
            }}
          >
            Nouvelle demande
          </Button>
        )}
      </Box>
      {error && <Alert severity="error" sx={{ mx: 2, mt: 1 }}>{error}</Alert>}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <MessagingConversationList
          conversations={conversations}
          selectedId={selectedId}
          query={query}
          onQueryChange={setQuery}
          onSelect={(conversation) => setSelectedId(conversation.id)}
          getIdentity={(conversation) => (
            conversation.subject || 'Support SALOONE'
          )}
          getPreview={(conversation) => (
            conversation.last_message?.content || 'Nouvelle demande'
          )}
          emptyTitle="Aucune demande"
          emptyDescription="Contactez le support SALOONE depuis cette messagerie."
        />
      </Box>
    </Box>
  );
};

export default SupportChatPanel;
